import { InformationCircleIcon, LockClosedIcon, TrophyIcon } from "@heroicons/react/24/outline";
import { useI18N, useResponsive } from "@sundae/react-hooks";
import {
  Avatar,
  AvatarGroup,
  ConditionalWrapper,
  Text,
  Tooltip,
  toolkitCx,
} from "@sundae/ui-toolkit";
import cx from "classnames";
import { FC } from "react";
import { useMarketOpenForPool } from "../../hooks/liquidity/useMarketOpenForPool";
import MarketOpenTooltip from "../../i18n/components/liquidity/MarketOpenTooltip";
import { TPool } from "../../types/Pool.types";
import { getAssetLogo, getQAsset } from "../../utils/assets.utils";
import { getPoolName } from "../../utils/pool.utils";
import PoolBadges from "../PoolBadges";
import { IRenderedBadgesConfig } from "../PoolBadges/PoolBadges";
import PoolHoverCard from "../PoolHoverCard";
import StarFavoritePoolIndicator from "../StarFavoritePoolIndicator";

export interface IPoolComponentProps {
  badges?: IRenderedBadgesConfig;
  inline?: boolean;
  isLocked?: boolean;
  isYieldFarming?: boolean;
  pool: TPool;
  renderBadges?: boolean;
  small?: boolean;
  withFavorites?: boolean;
}

/**
 * The Pool Component that renders the pool name, logo, and badges.
 *
 * @component
 * @param {IRenderedBadgesConfig} badges - The badges config which determines which badges to render. Merges the given badges with the `defaultBadgesConfig`. Defaults to `defaultBadgesConfig` (see `src/components/PoolBadges/PoolBadges.tsx`).
 * @param {boolean} inline - Whether to render the component inline or not.
 * @param {boolean} isLocked - Whether the pool is locked or not.
 * @param {boolean} isYieldFarming - Whether the pool is related to a YF position or not.
 * @param {TPool} pool - The pool to render.
 * @param {boolean} withFavorites - Whether to render the component with the favorites indicator or not.
 * @returns {React.ReactElement} - The Pool Component.
 * */
const PoolComponent: FC<IPoolComponentProps> = ({
  badges,
  inline,
  isLocked = false,
  isYieldFarming = false,
  pool,
  small = false,
  renderBadges = true,
  withFavorites = false,
}) => {
  const { t } = useI18N();
  const { sm, lg } = useResponsive();
  const { formattedMarketOpenDate, isMarketOpen /** marketOpenSlot */ } =
    useMarketOpenForPool(pool);
  const defaultAvatarSize = "sm";
  const assetAQAsset = getQAsset(pool.assetA.assetId);
  const assetBQAsset = getQAsset(pool.assetB.assetId);
  const isEveryBadgeFalse = Object.values(badges ?? {}).every((badge) => !badge);

  return (
    <div className="relative flex items-center gap-2">
      {withFavorites && sm && <StarFavoritePoolIndicator ident={pool.ident} />}
      <PoolHoverCard pool={pool} small={small}>
        <AvatarGroup>
          <Avatar
            size={small ? "xs" : defaultAvatarSize}
            src={getAssetLogo(assetAQAsset ?? pool?.assetA) || ""}
            stacked
          />
          <Avatar
            size={small ? "xs" : defaultAvatarSize}
            src={getAssetLogo(assetBQAsset ?? pool?.assetB) || ""}
            stacked
          />
        </AvatarGroup>
      </PoolHoverCard>
      <div
        className={cx("text-left", {
          "flex items-center gap-2": inline,
          "space-y-1": !inline,
        })}
      >
        <div className="relative flex items-center gap-2 leading-3">
          <div className="flex flex-wrap items-center gap-2">
            <Text size={small ? "xs" : "sm"} tag="span">
              {getPoolName({
                assetA: assetAQAsset ?? pool?.assetA,
                assetB: assetBQAsset ?? pool?.assetB,
              })}
            </Text>
            {!isMarketOpen && lg && (
              <>
                {inline ? (
                  <MarketOpenTooltip openDate={formattedMarketOpenDate}>
                    <LockClosedIcon className={cx("h-4 w-4", toolkitCx.layout.text.default)} />
                  </MarketOpenTooltip>
                ) : (
                  <Text
                    className="inline-flex flex-wrap items-center gap-1"
                    size={small ? "xs" : "sm"}
                    tag="span"
                    variant="silent"
                  >
                    {t("closed")}{" "}
                    <MarketOpenTooltip openDate={formattedMarketOpenDate}>
                      <InformationCircleIcon
                        className={cx("h-4 w-4", toolkitCx.layout.text.silent)}
                      />
                    </MarketOpenTooltip>
                  </Text>
                )}
              </>
            )}
            {isLocked && <LockClosedIcon className={toolkitCx.layout.text.default} width={16} />}
            {isYieldFarming && (
              <Tooltip
                trigger={<TrophyIcon className={toolkitCx.layout.text.default} width={16} />}
                tooltipLabel={t("migration.pool", { ns: "farms" })}
              />
            )}
          </div>

          {inline && !isEveryBadgeFalse && (
            <Text
              tag="span"
              size="xl"
              variant="silent"
              className={cx("absolute -right-4 opacity-40", toolkitCx.recipes.center.vertical)}
            >
              /
            </Text>
          )}
        </div>

        {renderBadges && (
          <ConditionalWrapper
            condition={!!inline}
            wrapper={(children) => <div className="ml-4">{children}</div>}
          >
            <PoolBadges badges={badges} pool={pool} />
          </ConditionalWrapper>
        )}
      </div>
    </div>
  );
};

export default PoolComponent;
